import React, { FC, useState } from 'react';
import {
  GetStaticPaths,
  InferGetStaticPropsType,
  GetStaticPropsContext,
} from 'next';

import Link from 'next/link';

import { SanityClient } from 'lib/sanity';
import { CaseStudyPages, Settings } from 'lib/sanity/types';

import CaseStudy from 'components/CaseStudy';
import Meta from 'components/Meta';
import { REVALIDATION_TIME } from 'constants/values';

const CaseStudyPage: FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  settings,
  caseStudyPage,
  caseStudyPageSlug,
  landingMenuSnippetPages,
  preview,
}) => {
  const { metaCard, slug } = caseStudyPage;

  return (
    <>
      <Meta seo={metaCard} />
      <CaseStudy
        caseStudyPage={caseStudyPage}
        settings={settings}
        landingMenuSnippetPages={landingMenuSnippetPages}
        key={caseStudyPageSlug}
      />
      {preview && (
        <Link href={`/api/exit-preview?middlePath=case-study&slug=${slug}`}>
          <div className="fixed bottom-0 right-0 m-8 bg-white shadow-md p-3 rounded-sm border cursor-pointer z-nav">
            Exit preview
          </div>
        </Link>
      )}
    </>
  );
};

const filterDataToSingleItem = (data: CaseStudyPages, preview: any) => {
  if (data.length === 1) {
    return data[0];
  }

  if (preview) {
    return data.find((item) => item._id.startsWith(`drafts.`)) || data[0];
  }

  return data[0];
};

export const getStaticPaths: GetStaticPaths = async () => {
  const caseStudyPageSlugs = await SanityClient.fetchCaseStudyPageSlugs();
  const paths = caseStudyPageSlugs!.map((page) => ({
    params: { caseStudyPageSlug: page.slug },
  }));

  return { paths, fallback: 'blocking' };
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const caseStudyPageSlug = context.params?.caseStudyPageSlug;
  const preview = context.preview ? context.preview : null;
  if (!caseStudyPageSlug || typeof caseStudyPageSlug !== 'string') {
    return {
      notFound: true,
    };
  }

  const [settings, caseStudyPage, landingMenuSnippetPages] = await Promise.all([
    SanityClient.fetchSettings(),
    filterDataToSingleItem(
      await SanityClient.fetchCaseStudyPage({
        slug: caseStudyPageSlug,
        preview: preview,
      }),
      preview,
    ),
    SanityClient.fetchLandingMenuSnippetPages(),
  ]);

  if (!settings || !caseStudyPage) {
    return {
      notFound: true,
    };
  }
  return {
    props: {
      settings,
      caseStudyPage,
      caseStudyPageSlug,
      landingMenuSnippetPages,
      preview,
      globals: {
        settings,
      },
    },
    revalidate: REVALIDATION_TIME,
  };
};

export default CaseStudyPage;
